import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';

import {
	actionCancelReportReview,
	actionApproveReport,
	actionRejectReport,
} from '../../../../redux/action/userSingleReport/actionUserSingleReport';
import {
	formatHistoryDateTime,
	formatDateTime,
} from '../../../../utils/formatDate';
import { getLabelByValue } from '../../../../utils/getLabelByValue';
import { AppDispatch } from '../../../../redux/reducer/store';

import Select from '../../../custom/select/select';

import ArrowIcon from '../../../../assets/icons/chevron.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close.svg';
import { IReportCriteriasOptions } from '../../../../types/reportsInterfaces';
import { IUserOptions } from '../../../../types/interfaces';

const statusOptions = [
	{ value: '', label: 'Выберите статус' },
	{ value: 'NotChecked', label: 'Не проверен' },
	{ value: 'PartiallyRejected', label: 'Отклонен' },
	{ value: 'Approved', label: 'Утвержден' },
];

const ReportCheckTableSection: FC = () => {
	const dispatch = useDispatch<AppDispatch>();

	const { criteriasList, report } = useSelector(
		(state: any) => state.userSingleReport.userSingleReport
	);

    const [isAvailableOsa, setAvailableOsa] = useState<boolean>(false);
    const [internalCheckCriterias, setInternalCheckCriterias] = useState<IReportCriteriasOptions[]>([]);

	const { claims } = useSelector((state: IUserOptions) => state.user.user.data);

	const [isFixedReportOpen, setIsFixedReportOpen] = useState<boolean>(false);
	const [isInternalReportRevisionOpen, setIsIntermalReportRevisionOpen] =
		useState<boolean>(false);
	const [isInternalReportImprovedOpen, setIsIntermalReportImprovedOpen] =
		useState<boolean>(false);
	const [isInternalReportApproveOpen, setIsIntermalReportApproveOpen] =
		useState<boolean>(false);
	const [isInternalReportRejectOpen, setIsIntermalReportRejectOpen] =
		useState<boolean>(false);
	const [isRejectedReportOpen, setIsRejectedReportOpen] =
		useState<boolean>(false);

	const [selectedStatusOption, setSelectedStatusOption] = useState<string>('');
	const [reviewCommentValue, setReviewCommentValue] = useState<string>('');
	const [commentValue, setCommentValue] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [selectedReviewOptionId, setSelectedReviewOptionId] =
		useState<number>(0);
	const [isError, setIsError] = useState<boolean>(false);
	const [isVisibleData, setIsVisibleData] = useState<boolean>(true);

    const [reasonOptions, setReasonOptions] = useState<any[]>([]);

    useEffect(() => {
        if(report) {
            setAvailableOsa(report.skus?.some((sku: any) => sku.signal && sku.signal?.hasOwnProperty("isAvailableOsa")))
        }

        if (criteriasList) {
            setInternalCheckCriterias(criteriasList.filter((item: IReportCriteriasOptions) => item.internalCheck || (item.rateOsa && isAvailableOsa)));
        }
    }, [report, criteriasList, isAvailableOsa]);

    useEffect(() => {
        if(internalCheckCriterias) {
            const reasonOptions = internalCheckCriterias.map((option: IReportCriteriasOptions) => ({
                value: option.reason.id || option,
                label: option.reason.name || option,
            }));
            reasonOptions.unshift({ value: 0, label: 'Выберите причину' });
            setReasonOptions(reasonOptions)
        }
    }, [internalCheckCriterias]);

	useEffect(() => {
		const reportStatus =
			report && Object.keys(report?.reviewModel?.reviewStatus).toString();

		const statusActions: any = {
			Approved: {
				setState: setIsIntermalReportApproveOpen,
				statusOption: 'Approved',
			},
			PartiallyRejected: {
				setState: setIsIntermalReportRejectOpen,
				statusOption: 'PartiallyRejected',
			},
			Revision: {
				setState: setIsIntermalReportRevisionOpen,
			},
			Improved: {
				setState: setIsIntermalReportImprovedOpen,
			},
			Rejected: {
				setState: setIsRejectedReportOpen,
			},
			Fixed: {
				setState: setIsFixedReportOpen,
			},
			NotChecked: {},
		};

		if (reportStatus) {
			handleClickInternalLink();

			const statusAction = statusActions[reportStatus];
			if (!!Object.keys(statusAction).length) {
				statusAction.setState(true);
				if (statusAction.statusOption) {
					setSelectedStatusOption(statusAction.statusOption);
				}
			}
		}
	}, [report?.reviewModel?.reviewStatus, report?.reportId?.id, report]);

	const handleClickInternalLink = () => {
		setIsIntermalReportImprovedOpen(false);
		setIsIntermalReportRevisionOpen(false);
		setIsIntermalReportApproveOpen(false);
		setIsIntermalReportRejectOpen(false);
		setIsRejectedReportOpen(false);
		setSelectedReviewOptionId(0);
		setIsFixedReportOpen(false);
		setSelectedStatusOption('');
		setReviewCommentValue('');
		setCommentValue('');
	};

	const formatRejectedDateTime = (newDateTime: string) => {
		const options: { [key: string]: string } = {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
		};

		const dateTime = newDateTime ? new Date(newDateTime) : new Date();
		return new Intl.DateTimeFormat('ru-RU', options).format(dateTime);
	};

	const handleChangeStatusOptions = (event: { value: string, label: string }) => {
		setSelectedStatusOption(event.value);
		if (errorMessage === 'Статус не выбран') {
			setIsError(false);
		}
	};

	const handleChangeReasonOptions = (event: { value: number, label: string }) => {
		setSelectedReviewOptionId(event.value);
		setIsError(false);
		if (errorMessage === 'Нужно указать причину') {
			setIsError(false);
		}
	};

    const validateForm = (): boolean => {
        let isValid = true;

        if (selectedStatusOption === '') {
            setErrorMessage('Статус не выбран');
            setIsError(true);
            isValid = false;
        }

        if (selectedStatusOption === 'PartiallyRejected' && selectedReviewOptionId === 0) {
            setErrorMessage('Нужно указать причину');
            setIsError(true);
            isValid = false;
        }

        if (selectedReviewOptionId > 0 && !(reviewCommentValue?.trim()?.length > 0)) {
            const reason = internalCheckCriterias?.find((o: IReportCriteriasOptions) => o.reason.id === selectedReviewOptionId);
            if (reason && reason?.internalCheckNote) {
                setErrorMessage('Нужно указать комментарий к проверке');
                setIsError(true);
                isValid = false;
            }
        }
        return isValid;
    }

    const handleClickSetUpBtn = () => {
        if (validateForm()) {
            if (selectedStatusOption === 'NotChecked') {
                dispatch(actionCancelReportReview(report && report.reportId.id)).then(
                    () => {
                        handleClickInternalLink();
                    }
                );
            } else if (selectedStatusOption === 'Approved') {
                dispatch(
                    actionApproveReport({
                                            reportId: report.reportId,
                                            reviewReasonId: {
                                                id: selectedReviewOptionId,
                                            },
                                            reviewComment: reviewCommentValue,
                                            comment: commentValue,
                                        })
                ).then(() => {
                    setIsIntermalReportApproveOpen(true);
                });
            } else if (selectedStatusOption === 'PartiallyRejected') {
                dispatch(
                    actionRejectReport({
                                           reportId: report.reportId,
                                           reviewReasonId: {
                                               id: selectedReviewOptionId,
                                           },
                                           reviewComment: reviewCommentValue,
                                           comment: commentValue,
                                       })
                );
            }
        }
    };

	const handleClickCancelBtn = () => {
		setSelectedReviewOptionId(0);
		setSelectedStatusOption('');
		setReviewCommentValue('');
		setCommentValue('');
	};

	const isAllSectionsClosed =
		!isInternalReportApproveOpen &&
		!isInternalReportRevisionOpen &&
		!isInternalReportImprovedOpen &&
		!isInternalReportRejectOpen &&
		!isRejectedReportOpen &&
		!isFixedReportOpen;

	const reviewComment = report && report?.reviewModel?.reviewComment;
	const reviewerData = report && report?.reviewModel?.reviewer;
	const comment = report && report?.reviewModel?.comment;
	const fixDate = report && report?.reviewModel?.fixDate;
	const reason = report && report?.reviewModel?.reason;
	const date = report && report?.reviewModel?.date;
	const reviewer = reviewerData
		? `${reviewerData.familyName || ''} 
         ${reviewerData?.givenName || ''} 
         ${reviewerData?.middleName || ''} 
         (${reviewerData?.role})`
		: 'Система';

	return (
		<div className={'customerReport__check_block'}>
			{isAllSectionsClosed && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Проверка отчета</span>
					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					{isError && (
						<div className='report__error_block'>
							<div className='error__block_row'>
								<span>
									Вы должны исправить следующие ошибки прежде, чем продолжить.
								</span>
								<CloseIcon
									onClick={() => setIsError(false)}
									className='close__icon'
								/>
							</div>
							<ul className='error__list'>
								<li>{errorMessage}</li>
							</ul>
						</div>
					)}
					{!(claims.includes('admin') || claims.includes('reports:review:create')) ? (
						<div className='test'>Отчет еще не проверен</div>
					) : (
						<>
							<div className='customer__check_block'>
								<div className='report__list_elem'>
									<span>Уровень проверки:</span>
									<span>Контролер</span>
								</div>
								<div className='report__list_elem'>
									<span>Статус:</span>
									<div className='wrapper__list_block'>
										<Select
											propsChange={handleChangeStatusOptions}
											defaultValue={statusOptions[0]}
											value={statusOptions.find(
												option => option.value === selectedStatusOption
											)}
											className='check__select'
											options={statusOptions}
										/>
									</div>
								</div>
								<div className='report__list_elem'>
									<span>Причина (отклонения/доработки):</span>
									<div className='wrapper__list_block'>
										<Select
											defaultValue={{ value: 0, label: 'Выберите причину' }}
											propsChange={handleChangeReasonOptions}
											value={
												reasonOptions &&
												reasonOptions.find(
													(option: { value: number, label: string }) =>
														option.value === selectedReviewOptionId
												)
											}
											className='check__select'
											options={reasonOptions}
										/>
									</div>
								</div>
								<div className='report__list_elem'>
									<span>Комментарий к проверке:</span>
									<textarea
										className='check__comment_textfield'
										onChange={event =>
											setReviewCommentValue(event.target.value)
										}
										value={reviewCommentValue}
									></textarea>
								</div>
								<div className='report__list_elem'>
									<span>Комментарий:</span>
									<textarea
										className='comment__textfield'
										onChange={event => setCommentValue(event.target.value)}
										value={commentValue}
									></textarea>
								</div>
							</div>
							<div className='report__check_btnBlock'>
								<button className='set__up_btn' onClick={handleClickSetUpBtn}>
									Установить
								</button>
								<button className='cancel__btn' onClick={handleClickCancelBtn}>
									Отмена
								</button>
								<div></div>
							</div>
						</>
					)}
				</div>
			)}
			{isInternalReportApproveOpen && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Внутренняя проверка отчета</span>
					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					<div className='customer__check_block'>
						<div className='internal__check_elem'>
							<span>Проверяющий:</span>
							<span>{reviewer}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Статус:</span>
							<span className='internal__link'>
								<span
									onClick={() => {
										if (
											claims.includes('admin') ||
											claims.includes('reports:review:create')
										) {
											handleClickInternalLink();
										}
									}}
									className={clsx({
										link__content:
											claims.includes('admin') ||
											claims.includes('reports:review:create'),
									})}
								>
									{getLabelByValue(selectedStatusOption, statusOptions)}
								</span>
							</span>
						</div>
						{comment && (
							<div className='internal__check_elem'>
								<span>Комментарий:</span>
								<span>
									<div className='disabled__revision_info'>
										<pre>{comment}</pre>
									</div>
								</span>
							</div>
						)}
						{reviewComment && (
							<div className='internal__check_elem'>
								<span>Комментарий при проверке:</span>
								<span>
									<input
										className='internal__check_input'
										type='text'
										readOnly
										disabled
										defaultValue={reviewComment}
									/>
								</span>
							</div>
						)}
						<div className='internal__check_elem'>
							<span>Дата:</span>
							<span>{formatHistoryDateTime(date)}</span>
						</div>
					</div>
				</div>
			)}
			{isInternalReportRejectOpen && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Внутренняя проверка отчета</span>

					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					<div className='customer__check_block'>
						<div className='internal__check_elem'>
							<span>Проверяющий:</span>
							<span>{reviewer}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Статус:</span>
							<span className='internal__link'>
								<span
									onClick={() => {
										if (
											claims.includes('admin') ||
											claims.includes('reports:review:create')
										) {
											handleClickInternalLink();
										}
									}}
									className={clsx({
										link__content:
											claims.includes('admin') ||
											claims.includes('reports:review:create'),
									})}
								>
									{getLabelByValue(selectedStatusOption, statusOptions)}{' '}
									(частично)
								</span>
							</span>
						</div>
						<div className='internal__check_elem'>
							<span>Критерий:</span>
							<span>
								<pre>
									{getLabelByValue(
                                        internalCheckCriterias?.find(
											(elem: IReportCriteriasOptions) => elem?.reason?.name === reason
										)?.reason?.id,
										reasonOptions
									) || reason}
								</pre>
							</span>
						</div>
						{comment && (
							<div className='internal__check_elem'>
								<span>Комментарий:</span>
								<div className='reportCheck__info_block'>
									<pre>{comment}</pre>
								</div>
							</div>
						)}
						{reviewComment && (
							<div className='internal__check_elem'>
								<span>Комментарий при проверке:</span>
								<span>
									<input
										className='internal__check_input'
										type='text'
										readOnly
										disabled
										defaultValue={reviewComment}
									/>
								</span>
							</div>
						)}
						<div className='internal__check_elem'>
							<span>Дата:</span>
							<span>{formatHistoryDateTime(date)}</span>
						</div>
					</div>
				</div>
			)}
			{isInternalReportRevisionOpen && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Внутренняя проверка отчета</span>

					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					<div className='revision__check_block'>
						<div className='internal__check_elem'>
							<span>Проверяющий:</span>
							<span>{reviewer}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Статус:</span>
							<span className='internal__link'>
								<span
									onClick={() => {
										if (
											claims.includes('admin') ||
											claims.includes('reports:review:create')
										) {
											handleClickInternalLink();
										}
									}}
									className={clsx({
										link__content:
											claims.includes('admin') ||
											claims.includes('reports:review:create'),
									})}
								>
									Доработка
								</span>
							</span>
						</div>
						<div className='internal__check_elem'>
							<span>Дата отклонения:</span>
							<span>{formatHistoryDateTime(fixDate)}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Критерий:</span>
							<span>{reason}</span>
						</div>
						{comment && (
							<div className='internal__check_elem'>
								<span>Комментарий:</span>
								<span>
									<div className='disabled__revision_info'>
										<pre>{comment}</pre>
									</div>
								</span>
							</div>
						)}
						{reviewComment && (
							<div className='internal__check_elem'>
								<span>Комментарий при проверке:</span>
								<span>
									<input
										className='internal__revision_input'
										type='text'
										readOnly
										disabled
										defaultValue={reviewComment}
									/>
								</span>
							</div>
						)}
						<div className='internal__check_elem'>
							<span>Дата:</span>
							<span>{date && formatDateTime(date)}</span>
						</div>
					</div>
				</div>
			)}
			{isInternalReportImprovedOpen && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Внутренняя проверка отчета</span>

					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					<div className='revision__check_block'>
						<div className='internal__check_elem'>
							<span>Проверяющий:</span>
							<span>{reviewer}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Статус:</span>
							<span className='internal__link'>
								<span
									onClick={() => {
										if (
											claims.includes('admin') ||
											claims.includes('reports:review:create')
										) {
											handleClickInternalLink();
										}
									}}
									className={clsx({
										link__content:
											claims.includes('admin') ||
											claims.includes('reports:review:create'),
									})}
								>
									Доработан
								</span>
							</span>
						</div>
						<div className='internal__check_elem'>
							<span>Критерий:</span>
							<span>{reason}</span>
						</div>
						{comment && (
							<div className='internal__check_elem'>
								<span>Комментарий:</span>
								<span>
									<div className='disabled__revision_info'>
										<pre>{comment}</pre>
									</div>
								</span>
							</div>
						)}
						{reviewComment && (
							<div className='internal__check_elem'>
								<span>Комментарий при проверке:</span>
								<span>
									<input
										className='internal__revision_input'
										type='text'
										readOnly
										disabled
										defaultValue={reviewComment}
									/>
								</span>
							</div>
						)}
						<div className='internal__check_elem'>
							<span>Дата:</span>
							<span>{formatHistoryDateTime(date)}</span>
						</div>
					</div>
				</div>
			)}
			{isRejectedReportOpen && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Внутренняя проверка отчета</span>

					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					<div className='revision__check_block'>
						<div className='internal__check_elem'>
							<span>Проверяющий:</span>
							<span>{reviewer}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Статус:</span>
							<span className='internal__link'>
								<span
									onClick={() => {
										if (
											claims.includes('admin') ||
											claims.includes('reports:review:create')
										) {
											handleClickInternalLink();
										}
									}}
									className='reject__link_content'
								>
									Отклонен
								</span>
							</span>
						</div>
						<div className='internal__check_elem'>
							<span>Критерий:</span>
							<span>{reason}</span>
						</div>
						{comment && (
							<div className='internal__check_elem'>
								<span>Комментарий:</span>
								<div className='reportCheck__info_block'>
									<pre>{comment}</pre>
								</div>
							</div>
						)}
						{reviewComment && (
							<div className='internal__check_elem'>
								<span>Комментарий при проверке:</span>
								<span>
									<input
										className='internal__revision_input'
										type='text'
										readOnly
										disabled
										defaultValue={reviewComment}
									/>
								</span>
							</div>
						)}
						<div className='internal__check_elem'>
							<span>Дата:</span>
							<span>{formatHistoryDateTime(date)}</span>
						</div>
					</div>
				</div>
			)}
			{isFixedReportOpen && (
				<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
					<span className='section__title inside'>Внутренняя проверка отчета</span>

					<button
						className='sectionData_button'
						onClick={() => setIsVisibleData(!isVisibleData)}
					>
						<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
					</button>
					<div className='revision__check_block'>
						<div className='internal__check_elem'>
							<span>Проверяющий:</span>
							<span>{reviewer}</span>
						</div>
						<div className='internal__check_elem'>
							<span>Статус:</span>
							<span className='internal__link'>
								<span
									onClick={() => {
										if (
											claims.includes('admin') ||
											claims.includes('reports:review:create')
										) {
											handleClickInternalLink();
										}
									}}
									className='fixed__link_content'
								>
									Исправлен
								</span>
							</span>
						</div>
						<div className='internal__check_elem'>
							<span>Дата:</span>
							<span>{date && formatRejectedDateTime(date)}</span>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ReportCheckTableSection;
